import { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import {
  DollarSign,
  Loader2,
  Crown,
  TrendingUp,
  RefreshCw,
  Handshake,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../lib/toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RevenueCard = ({ title, value, subtitle, icon: Icon, color = 'green' }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">
          ${typeof value === 'number' ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value}
        </p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
    </div>
  </div>
);

const CountCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
    </div>
  </div>
);

export default function AdminRevenuePage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [revenue, setRevenue] = useState(null);

  useEffect(() => {
    fetchRevenue();
  }, [token]);

  const fetchRevenue = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/analytics/revenue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevenue(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required for revenue data');
      } else {
        toast.error('Failed to load revenue data');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading && !revenue) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-green-600" />
              Billing & Revenue
            </h1>
            <p className="text-gray-500">Platform revenue and subscription metrics</p>
          </div>
          <Button onClick={fetchRevenue} variant="outline" className="gap-2" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Total Revenue */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-6 mb-6 text-white">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-green-200" />
            <span className="text-green-100 font-medium">Total Revenue</span>
          </div>
          <p className="text-4xl font-bold">
            ${(revenue?.estimated_total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-green-200 text-sm mt-2">From WANTD+ subscriptions</p>
        </div>

        {/* Revenue Breakdown */}
        <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <RevenueCard
            title="Premium Subscriptions"
            value={revenue?.premium_subscription_revenue || 0}
            subtitle={`${revenue?.monthly_subscribers || 0} subscribers × $5.00/mo`}
            icon={Crown}
            color="amber"
          />
        </div>

        {/* Stats */}
        <h3 className="text-lg font-bold text-gray-900 mb-4">Platform Stats</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <CountCard
            title="Active Premium Subscribers"
            value={revenue?.monthly_subscribers || 0}
            subtitle="$5.00/month WANTD+"
            icon={Crown}
            color="amber"
          />
          <CountCard
            title="Completed Deals"
            value={revenue?.completed_deals || 0}
            subtitle="Transactions completed offline"
            icon={Handshake}
            color="green"
          />
        </div>
      </div>
    </AdminLayout>
  );
}
